import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { BlackListService } from '../../../services/blacklist.service';
import { AlertService } from '../../../services/alert.service';
import { CompaniesService } from '../../../services/companies.service';
import { AccountsService } from '../../../services/accounts.service';
import useBgPages from '../../../hooks/use-bg-pages.hook';
import Aside from '../../../components/layout/asaid.component';
import Header from '../../../components/layout/header.component';
import Footer from '../../../components/layout/footer.component';
import Icon from '../../../components/forms/icon.component';
import Title from '../../../components/forms/titles.component';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const CampaignsMode: React.FC = () => {
  useBgPages();
  const [listBlacklist, setListBlacklist] = useState<{ value: string; label: string }[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [wabaAccounts, setWabaAccounts] = useState<any[]>([]);
  const [emailAccounts, setEmailAccounts] = useState<any[]>([]);
  const [rcsAgents, setRcsAgents] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [masks, setMasks] = useState<any[]>([]);

  const location = useLocation();
  const structure = location.state?.structure;

  const zone = [
    { value: 'America/Tijuana', label: '(UTC-8:00) Tijuana, Baja California' },
    {
      value: 'America/Sinaloa',
      label: '(UTC-7:00) Hermosillo, Sonora, Mazatlán, Sinaloa, Tepic, Nayarit'
    },
    {
      value: 'America/Mexico_City',
      label: '(UTC-6:00) Ciudad de México, Guadalajara, Monterrey'
    },
    { value: 'America/Cancun', label: '(UTC-5:00) Cancún, Quintana Roo' }
  ];
  const isFlowType = structure?.type === 'FLOW';
  const extractData = () => {
    if (!structure) return {};

    if (isFlowType) {
      return {
        name: structure.name,
        type: structure.type,
        companie: null,
        account: structure.account,
        user: structure.user,
        send_single_messages: structure.send_single_messages,
        message_schedule: structure.message_schedule,
        message_date_window: structure.message_date_window,
        blacklists: structure.blacklists || [],
        goal: structure.goal,
        default_channel: structure.default_channel,
        flows: structure.flows,
        db: structure.db,
        phone_number_column: structure.phone_number_column
      };
    } else {
      const {
        name,
        type,
        companie,
        send_single_messages,
        message_schedule,
        message_date_window,
        blacklists,
        account,
        channels = []
      } = structure;

      return {
        name,
        type,
        companie,
        account,
        send_single_messages,
        message_schedule,
        message_date_window,
        blacklists,
        channels
      };
    }
  };

  const data = extractData();
  const getChannelsFromFlows = () => {
    if (!isFlowType || !data.flows) return [];

    const channelOrder = [
      'whatsapp',
      'rcs_conversational',
      'rcs_single',
      'rcs_basic',
      'sms',
      'email'
    ];
    const channels: any[] = [];

    channelOrder.forEach((channelKey, index) => {
      if (data.flows[channelKey]) {
        channels.push({
          type: channelKey.toUpperCase(),
          index: index,
          config: data.flows[channelKey]
        });
      }
    });

    return channels;
  };

  const displayChannels = isFlowType ? getChannelsFromFlows() : data.channels || [];
  const getBlackList = async () => {
    try {
      const response = await BlackListService.getBlackList();
      const formatted = response
        .filter((item: any) => item.status === true)
        .map((item: any) => ({
          value: item._id,
          label: item.name
        }));
      setListBlacklist(formatted);
    } catch (errorCode: any) {
      console.error('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const getCompanies = async () => {
    try {
      const data = await CompaniesService.getCompanies();
      setCompanies(
        data.map((item: any) => ({
          value: item._id,
          label: item.name
        }))
      );
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
      setCompanies([]);
    }
  };

  const getAccount = async () => {
    try {
      if (data.companie) {
        const accountData = await AccountsService.getAccountsForCompany(data.companie);
        setAccounts(
          accountData.map((item: any) => ({
            value: item._id,
            label: item.name,
            companie: item.company
          }))
        );
      }
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };
  const getBlacklistName = (id: string) => {
    const match = listBlacklist.find((item) => item.value === id);
    return match?.label || id;
  };

  const getCompanyName = (id: string) => {
    const match = companies.find((item) => item.value === id);
    return match?.label || id;
  };

  const getAccountName = (id: string) => {
    const match = accounts.find((item) => item.value === id);
    return match?.label || id;
  };

  const getChannelStyle = (channelType: string) => {
    if (channelType === 'default' || channelType === 'DEFAULT') {
      return 'bg-light-warning';
    }
    return 'bg-light-primary';
  };

  const getTimezoneLabel = (timezone: string) => {
    const zoneMatch = zone.find((item) => item.value === timezone);
    return zoneMatch ? zoneMatch.label : timezone;
  };

  const formatChannelType = (type: string) => {
    const typeUpper = type.toUpperCase();

    switch (typeUpper) {
      case 'RCS_CONVERSATIONAL':
        return 'RCS Conversacional';
      case 'RCS_SINGLE':
        return 'RCS Simple';
      case 'RCS_BASIC':
        return 'RCS Básico';
      case 'WHATSAPP':
        return 'WhatsApp';
      case 'SMS':
        return 'SMS';
      case 'EMAIL':
        return 'Email';
      default:
        return type;
    }
  };

  const getChannelIcon = (type: string) => {
    const typeUpper = type.toUpperCase();

    switch (typeUpper) {
      case 'RCS_CONVERSATIONAL':
      case 'RCS_SINGLE':
      case 'RCS_BASIC':
        return <Icon icon="ki-messages" size="fs-1" type="primary" className="fw-bolder" />;
      case 'WHATSAPP':
        return <Icon icon="ki-whatsapp" size="fs-1" type="primary" className="fw-bolder" />;
      case 'SMS':
        return <Icon icon="ki-message-text-2" size="fs-1" type="primary" className="fw-bolder" />;
      case 'EMAIL':
        return <Icon icon="ki-sms" size="fs-1" type="primary" className="fw-bolder" />;
      default:
        return null;
    }
  };
  const renderChannelEvents = (channelConfig: any, channelType: string) => {
    if (!channelConfig) return null;

    const events = [];
    if (channelConfig.on_error?.status) {
      events.push(
        <div key="error" className="mb-2">
          <span className="badge badge-light-danger me-2">
            <Icon icon="ki-cross-circle" size="fs-8" className="me-1" />
            Fallback
          </span>
          <span className="text-muted">→ {formatChannelType(channelConfig.on_error.channel)}</span>
        </div>
      );
    }
    if (channelConfig.on_follow_up?.status) {
      events.push(
        <div key="followup" className="mb-2">
          <span className="badge badge-light-info me-2">
            <Icon icon="ki-message-text-2" size="fs-8" className="me-1" />
            Seguimiento
          </span>
          <span className="text-muted">
            {channelConfig.on_follow_up.time} min
            {channelConfig.on_follow_up.on_sent?.status && ' (Enviado)'}
            {channelConfig.on_follow_up.on_delivered?.status && ' (Entregado)'}
          </span>
        </div>
      );
    }
    if (channelConfig.on_change_channel?.status) {
      events.push(
        <div key="change" className="mb-2">
          <span className="badge badge-light-warning me-2">
            <Icon icon="ki-arrow-up-down" size="fs-8" className="me-1" />
            Cambio
          </span>
          <span className="text-muted">
            {channelConfig.on_change_channel.time} min →{' '}
            {formatChannelType(channelConfig.on_change_channel.channel)}
          </span>
        </div>
      );
    }

    return events.length > 0 ? (
      <div className="mt-2">
        <small className="text-muted fw-bold">Eventos:</small>
        {events}
      </div>
    ) : null;
  };

  const generatePDF = async () => {
    const titleElement = document.getElementById('campaign-summary-title');
    const bodyElement = document.getElementById('campaign-summary-body');

    if (!titleElement || !bodyElement) return;

    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '-9999px';
    container.style.background = '#fff';
    container.style.padding = '20px';
    container.style.width = '1200px';
    container.style.zIndex = '-1';

    const clonedTitle = titleElement.cloneNode(true);
    const clonedBody = bodyElement.cloneNode(true);

    container.appendChild(clonedTitle);
    container.appendChild(clonedBody);
    document.body.appendChild(container);

    const canvas = await html2canvas(container);
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF();
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    const logo = new Image();
    logo.src = '/assets/media/logos/ch-l.png';
    logo.onload = () => {
      pdf.addImage(logo, 'SVG', 0, 10, 35, 20);
      pdf.addImage(imgData, 'PNG', 0, 35, pdfWidth, pdfHeight);
      pdf.save('campaña_chatbridge.pdf');
      document.body.removeChild(container);
    };
  };

  useEffect(() => {
    getBlackList();
    getCompanies();
    getAccount();
  }, []);

  const sortedChannels = [...displayChannels].sort((a, b) => a.index - b.index);

  return (
    <div className="page d-flex flex-row flex-column-fluid">
      <Aside />
      <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
        <Header />
        <div
          className="content fs-6 d-flex flex-column flex-column-fluid bg-campaigns"
          id="kt_content"
        >
          <div className="container-xxl">
            <div className="d-flex flex-column justify-content-center align-items-start px-5 py-10">
              <div className="mb-0" id="campaign-summary-title">
                <div className="d-flex align-items-center mb-2">
                  <Icon icon="ki-medal-star" size="fs-3x" type="primary" />
                  <h1 className="fw-bold fs-3x ms-2 mb-0">
                    Resumen <span className="ms-2">de tu</span>{' '}
                    <span className="text-info">campaña</span>
                  </h1>
                </div>
                <p className="fs-4 text-dark">Esta fue la configuración de tu campaña</p>
              </div>

              <div className="d-flex flex-column flex-md-row gap-4 mt-4 mb-10">
                <button className="btn btn-info fw-bolder px-10 py-3 btn_pdf" onClick={generatePDF}>
                  Descargar Resumen
                </button>
                <Link to="/sendCampaigns" className="btn btn-primary fw-bolder px-10 py-3">
                  Crear campaña
                </Link>
              </div>

              <div className="card shadow-sm p-5 w-100 mb-10" id="campaign-summary-body">
                <div className="card-body pe-md-5">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="ms-10">
                        <Title
                          icon="ki-gear"
                          title={'Configuración'}
                          subtitle={
                            'Verifica todas las configuraciones que realizaste al crear tu campaña.'
                          }
                          type="info"
                        />

                        <p className="fs-3 mb-3">
                          <strong>Nombre:</strong> {data.name}
                        </p>

                        <p className="fs-3 mb-3">
                          <strong>Tipo:</strong>{' '}
                          {data.type === 'DEFAULT'
                            ? 'Estándar'
                            : data.type === 'FALLBACK'
                              ? 'Fallback'
                              : data.type === 'FLOW'
                                ? 'Flujo de Mensajes'
                                : data.type}
                        </p>

                        {!isFlowType && data.companie && (
                          <p className="fs-3 mb-3">
                            <strong>Compañía:</strong> {getCompanyName(data.companie)}
                          </p>
                        )}

                        {isFlowType && (
                          <>
                            <p className="fs-3 mb-3">
                              <strong>Base de datos:</strong> {data.db}
                            </p>
                            <p className="fs-3 mb-3">
                              <strong>Canal por defecto:</strong>{' '}
                              {formatChannelType(data.default_channel)}
                            </p>
                            <p className="fs-3 mb-3">
                              <strong>Objetivo:</strong>{' '}
                              {data.goal === 'DELIVERED' ? 'Entregado' : data.goal}
                            </p>
                          </>
                        )}

                        <p className="fs-3 mb-3">
                          <strong>Cuenta:</strong> {getAccountName(data.account)}
                        </p>

                        <p className="fs-3 mb-3">
                          <strong>Mensajes únicos:</strong>{' '}
                          {data.send_single_messages ? 'Sí' : 'No'}
                        </p>

                        <p className="fs-3 mb-3">
                          <strong>Programada:</strong>{' '}
                          {data.message_schedule
                            ? data.message_schedule.start_date
                            : 'No configurado'}
                        </p>

                        <p className="fs-3 mb-3">
                          <strong>Zona horaria:</strong>{' '}
                          {data.message_schedule
                            ? getTimezoneLabel(data.message_schedule.timezone)
                            : 'No configurado'}
                        </p>

                        <p className="fs-3 mb-3">
                          <strong>Dosificada:</strong>{' '}
                          {data.message_date_window
                            ? `${data.message_date_window.start_date} a ${data.message_date_window.end_date}`
                            : 'No configurado'}
                        </p>

                        <p className="fs-3 mb-3">
                          <strong>Listas negras:</strong>{' '}
                          {data.blacklists?.length > 0 ? (
                            <ul className="mt-2">
                              {data.blacklists.map((id: string) => (
                                <li key={id}>{getBlacklistName(id)}</li>
                              ))}
                            </ul>
                          ) : (
                            'No configurado'
                          )}
                        </p>

                        {isFlowType ? (
                          <div className="mt-5">
                            <h5 className="fs-3 mb-3">Flujo de canales configurados:</h5>
                            <div className="d-flex flex-column gap-3 mt-3">
                              {sortedChannels.map((channel, i) => (
                                <div
                                  key={i}
                                  className="card border-2 border-dashed border-primary p-3"
                                >
                                  <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center">
                                      <span
                                        className={`badge badge-light-primary me-3 fs-6 px-3 py-2`}
                                      >
                                        {i + 1}
                                      </span>
                                      <div>
                                        <div className="d-flex align-items-center">
                                          <span className="fw-bold fs-5 text-dark me-2">
                                            {formatChannelType(channel.type)}
                                          </span>
                                          {getChannelIcon(channel.type)}
                                        </div>

                                        {renderChannelEvents(channel.config, channel.type)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : data.type === 'FALLBACK' ? (
                          <div className="mt-5">
                            <h5 className="fs-3 mb-3">Orden de envío de canales:</h5>
                            <div className="d-flex align-items-center flex-wrap gap-4 mt-3">
                              {sortedChannels.map((channel, i) => (
                                <div key={i} className="d-flex align-items-center">
                                  <div
                                    className={`d-flex align-items-center ${getChannelStyle(channel.type)} rounded p-3 shadow-sm`}
                                  >
                                    <span className="d-flex align-items-center ms-2 fw-bold fs-5 text-dark">
                                      Canal {channel.index + 1}: {formatChannelType(channel.type)}{' '}
                                      <span className="ms-2">{getChannelIcon(channel.type)}</span>
                                    </span>
                                  </div>
                                  {i < sortedChannels.length - 1 && channel.type !== 'default' && (
                                    <span className="mx-2 fs-3">➡️</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="mt-5">
                            <h5 className="fs-3 mb-3">
                              Canal de envío:
                              <span
                                className={`ms-2 fw-bold fs-5 text-dark ${getChannelStyle(displayChannels[0]?.type)} rounded p-3 shadow-sm`}
                              >
                                {formatChannelType(displayChannels[0]?.type)}{' '}
                                <span className="ms-2">
                                  {getChannelIcon(displayChannels[0]?.type)}
                                </span>
                              </span>
                            </h5>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6 d-flex flex-column align-items-center justify-content-center mt-20">
                      <img
                        src="/assets/media/illustrations/undraw/resume.svg"
                        alt="Resumen campaña"
                        id="campaign-summary-img"
                        className="mb-5 img-fluid"
                        style={{ maxHeight: '320px' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default CampaignsMode;
